// The agent-scoped option reads every batch-spec author needs (docs/14 §5: a
// Template IS a saved launch spec, so template authoring and the ad-hoc
// launch form pick from the SAME lists through the SAME path). Extracted out
// of the /simulations/new container so a second authoring route cannot drift
// into a differently-filtered or differently-labelled option set.
//
// Every list is filtered by `agent_id` server-side AND re-filtered client-side
// on `agent_id`: a cached page from a previous scope must never leak an
// option that belongs to another agent into this agent's form.
import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { requestErrorDescription } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";
import { useListQuery } from "../../lib/useListQuery";
import { useVariableDeclarations } from "../../lib/useVariableDeclarations";
import { interfaceLabel } from "../agent/lib/connectionConfig";
import type { VariableDeclaration } from "./simulationLaunchState";

export interface LaunchOption {
  id: string;
  label: string;
}

export interface LaunchOptionSets {
  connectionOptions: LaunchOption[];
  personaOptions: LaunchOption[];
  scenarioOptions: LaunchOption[];
  scenarioSetOptions: LaunchOption[];
  templateOptions: LaunchOption[];
  versionOptions: LaunchOption[];
}

export interface LaunchOptionsResult {
  /** First read error, if any — one message, not a stack of six. */
  errorMessage?: string;
  isPending: boolean;
  isRefreshingVariables: boolean;
  retry: () => void;
  options: LaunchOptionSets;
  /** Declared variables, needed to type sweep overrides at the boundary. */
  variables: VariableDeclaration[];
}

export function useLaunchOptions(agentId: string): LaunchOptionsResult {
  const client = useQueryClient();
  const agentVersions = useListQuery("/v1/agent-versions", {
    params: { agent_id: agentId },
    queryKey: queryKeys.agentVersions.list({ agent_id: agentId }),
  });
  const connections = useListQuery("/v1/connections", {
    params: { agent_id: agentId },
    queryKey: queryKeys.connections.list({ agent_id: agentId }),
  });
  const personas = useListQuery("/v1/personas", {
    params: { agent_id: agentId },
    queryKey: queryKeys.personas.list({ agent_id: agentId }),
  });
  const scenarioSets = useListQuery("/v1/scenario-sets", {
    params: { agent_id: agentId },
    queryKey: queryKeys.scenarioSets.list({ agent_id: agentId }),
  });
  const scenarios = useListQuery("/v1/scenarios", {
    params: { agent_id: agentId },
    queryKey: queryKeys.scenarios.list({ agent_id: agentId }),
  });
  const templates = useListQuery("/v1/templates", {
    params: { agent_id: agentId },
    queryKey: queryKeys.templates.list({ agent_id: agentId }),
  });
  const variables = useVariableDeclarations(agentId);

  const versionOptions = useMemo(
    () =>
      agentVersions.items
        .filter((item) => item.agent_id === agentId)
        .sort((left, right) => right.created_at.localeCompare(left.created_at))
        .map((item) => ({ id: item.id, label: item.tag })),
    [agentId, agentVersions.items],
  );
  const connectionOptions = useMemo(
    () =>
      connections.items
        .filter((item) => item.agent_id === agentId)
        .sort((left, right) => left.created_at.localeCompare(right.created_at))
        .map((item) => ({
          id: item.id,
          label: `${item.display_name} (${interfaceLabel(item)})`,
        })),
    [agentId, connections.items],
  );
  const personaOptions = useMemo(
    () =>
      personas.items
        .filter((item) => item.agent_id === agentId)
        .map((item) => ({ id: item.id, label: item.display_name })),
    [agentId, personas.items],
  );
  const scenarioSetOptions = useMemo(
    () =>
      scenarioSets.items
        .filter((item) => item.agent_id === agentId)
        .map((item) => ({ id: item.id, label: item.display_name })),
    [agentId, scenarioSets.items],
  );
  const scenarioOptions = useMemo(
    () =>
      scenarios.items
        .filter((item) => item.agent_id === agentId)
        .map((item) => ({ id: item.id, label: item.display_name })),
    [agentId, scenarios.items],
  );
  const templateOptions = useMemo(
    () =>
      templates.items.map((item) => ({
        id: item.id,
        label: item.display_name,
      })),
    [templates.items],
  );

  const options = useMemo(
    () => ({
      connectionOptions,
      personaOptions,
      scenarioOptions,
      scenarioSetOptions,
      templateOptions,
      versionOptions,
    }),
    [
      connectionOptions,
      personaOptions,
      scenarioOptions,
      scenarioSetOptions,
      templateOptions,
      versionOptions,
    ],
  );

  return {
    isRefreshingVariables: variables.isFetching,
    retry: () => {
      const keys = [
        queryKeys.agentVersions.list({ agent_id: agentId }),
        queryKeys.connections.list({ agent_id: agentId }),
        queryKeys.personas.list({ agent_id: agentId }),
        queryKeys.scenarios.list({ agent_id: agentId }),
        queryKeys.scenarioSets.list({ agent_id: agentId }),
        queryKeys.templates.list({ agent_id: agentId }),
        queryKeys.variables.allForAgent(agentId),
      ];
      for (const queryKey of keys) {
        void client.refetchQueries(
          { queryKey, exact: true },
          { cancelRefetch: false },
        );
      }
    },
    errorMessage:
      agentVersions.errorMessage ??
      connections.errorMessage ??
      personas.errorMessage ??
      scenarios.errorMessage ??
      scenarioSets.errorMessage ??
      templates.errorMessage ??
      (variables.error ? requestErrorDescription(variables.error) : undefined),
    isPending:
      agentVersions.isPending ||
      connections.isPending ||
      personas.isPending ||
      scenarios.isPending ||
      scenarioSets.isPending ||
      templates.isPending ||
      variables.isPending,
    options,
    variables: variables.data ?? [],
  };
}
